import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import axios from 'axios';
import { useCart } from '../context/CartContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const PaymentSuccess = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { clearCart } = useCart();
  const [status, setStatus] = useState('checking'); // checking, success, error
  const [paymentDetails, setPaymentDetails] = useState(null);
  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    const checkPaymentStatus = async () => {
      if (!sessionId) {
        setStatus('error');
        return;
      }

      let attempts = 0;
      const maxAttempts = 5;

      const pollStatus = async () => {
        try {
          const response = await axios.get(
            `${BACKEND_URL}/api/payments/stripe/status/${sessionId}`
          );

          if (response.data.payment_status === 'paid') {
            setStatus('success');
            setPaymentDetails(response.data);
            clearCart();
            return true;
          } else if (response.data.status === 'expired') {
            setStatus('error');
            return true;
          }

          attempts++;
          if (attempts < maxAttempts) {
            setTimeout(pollStatus, 2000); // Poll every 2 seconds
          } else {
            setStatus('error');
          }
        } catch (error) {
          console.error('Error checking payment status:', error);
          attempts++;
          if (attempts < maxAttempts) {
            setTimeout(pollStatus, 2000);
          } else {
            setStatus('error');
          }
        }
      };

      pollStatus();
    };

    checkPaymentStatus();
  }, [sessionId, clearCart]);

  if (status === 'checking') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center py-12 px-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-8 pb-6 text-center">
            <Loader2 className="h-16 w-16 text-blue-600 animate-spin mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-gray-900 mb-2">
              Verifying Payment...
            </h1>
            <p className="text-gray-600">
              Please wait while we confirm your payment
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center py-12 px-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-8 pb-6 text-center">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-red-600 text-3xl">✕</span>
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">
              Payment Verification Failed
            </h1>
            <p className="text-gray-600 mb-6">
              We couldn't verify your payment. Please contact support if you were charged.
            </p>
            <div className="space-y-3">
              <Button 
                onClick={() => navigate('/products')} 
                className="w-full bg-orange-600 hover:bg-orange-700"
              >
                Continue Shopping
              </Button>
              <Button 
                variant="outline" 
                onClick={() => navigate('/contact')}
                className="w-full"
              >
                Contact Support
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center py-12 px-4">
      <Card className="max-w-md w-full">
        <CardContent className="pt-8 pb-6 text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="h-10 w-10 text-green-600" />
          </div>
          
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            Payment Successful!
          </h1>
          
          <p className="text-gray-600 mb-2">
            Thank you for your order. Your payment has been processed successfully.
          </p>

          {paymentDetails && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6 mt-4">
              <p className="text-sm text-gray-700">
                <strong>Amount Paid:</strong> KES {(paymentDetails.amount_total / 100).toLocaleString()}
              </p>
              <p className="text-sm text-gray-700 mt-1">
                <strong>Status:</strong> {paymentDetails.payment_status}
              </p>
            </div>
          )}

          <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
            <p className="text-sm text-gray-700">
              📧 A confirmation email has been sent to your email address with order details.
            </p>
          </div>

          <div className="space-y-3">
            <Button 
              onClick={() => navigate('/products')} 
              className="w-full bg-orange-600 hover:bg-orange-700"
            >
              Continue Shopping
            </Button>
            <Button 
              variant="outline" 
              onClick={() => navigate('/dashboard')}
              className="w-full"
            >
              View My Orders
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default PaymentSuccess;
